import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Package, ClipboardList, Clock, Printer, Truck, CheckCircle2, Eye, User, Calendar, ArrowRight, Handshake, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND = RUNTIME_BACKEND_URL;

export default function AlbumcuDashboard() {
  const { token } = useAuthStore();
  const [stats, setStats] = useState({
    products: 0, total: 0, gonderildi: 0, isleme_alindi: 0, baskida: 0, kargoda: 0, teslim_edildi: 0
  });
  const [incoming, setIncoming] = useState([]);
  const [partnershipRequests, setPartnershipRequests] = useState([]);
  const [processing, setProcessing] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [p, o, pr] = await Promise.all([
        fetch(`${BACKEND}/api/albumcu/products/me`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/print-orders`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/albumcu-partnerships/incoming`, { headers }).then(r => r.json()),
      ]);
      const ss = { products: (p || []).length, total: (o || []).length,
        gonderildi: 0, isleme_alindi: 0, baskida: 0, kargoda: 0, teslim_edildi: 0 };
      (o || []).forEach(x => { if (ss[x.status] !== undefined) ss[x.status]++; });
      setStats(ss);
      setIncoming((o || []).filter(x => x.status === 'gonderildi'));
      setPartnershipRequests((pr || []).filter(x => x.status === 'pending'));
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const respondPartnership = async (pid, action) => {
    setProcessing(p => ({ ...p, [pid]: true }));
    try {
      const res = await fetch(`${BACKEND}/api/albumcu-partnerships/${pid}/${action}`, {
        method: 'PUT', headers
      });
      if (!res.ok) throw new Error();
      toast.success(action === 'accept' ? 'Anlaşma kabul edildi' : 'Anlaşma reddedildi');
      setPartnershipRequests(prev => prev.filter(x => x.id !== pid));
    } catch { toast.error('İşlem başarısız'); }
    finally { setProcessing(p => ({ ...p, [pid]: false })); }
  };

  const takeOrder = async (o) => {
    setProcessing(p => ({ ...p, [o.id]: true }));
    try {
      const res = await fetch(`${BACKEND}/api/print-orders/${o.id}/status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'isleme_alindi', note: 'İşleme alındı' })
      });
      if (!res.ok) throw new Error();
      toast.success('Sipariş işleme alındı');
      // Dashboard'dan kaldır
      setIncoming(prev => prev.filter(x => x.id !== o.id));
      setStats(s => ({ ...s, gonderildi: Math.max(0, s.gonderildi - 1), isleme_alindi: s.isleme_alindi + 1 }));
    } catch {
      toast.error('İşlem başarısız');
    } finally {
      setProcessing(p => ({ ...p, [o.id]: false }));
    }
  };

  const cards = [
    { label: 'Ürünlerim', value: stats.products, icon: Package, color: 'text-blue-600 bg-blue-50' },
    { label: 'Toplam Sipariş', value: stats.total, icon: ClipboardList, color: 'text-primary bg-primary/10' },
    { label: 'Yeni Gelen', value: stats.gonderildi, icon: Clock, color: 'text-slate-600 bg-slate-100', highlight: stats.gonderildi > 0 },
    { label: 'İşleme Alındı', value: stats.isleme_alindi, icon: ClipboardList, color: 'text-blue-600 bg-blue-50' },
    { label: 'Baskıda', value: stats.baskida, icon: Printer, color: 'text-purple-600 bg-purple-50' },
    { label: 'Kargoda', value: stats.kargoda, icon: Truck, color: 'text-amber-600 bg-amber-50' },
    { label: 'Teslim Edildi', value: stats.teslim_edildi, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
  ];

  return (
    <div className="space-y-6" data-testid="albumcu-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Gösterge Paneli</h1>
        <p className="text-sm text-slate-500">Baskı siparişlerinize hızlı bakış</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c, i) => {
          const Ic = c.icon;
          return (
            <Card key={i} className={c.highlight ? 'ring-2 ring-amber-400 shadow-md' : ''}>
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color} mb-2`}>
                  <Ic className="h-5 w-5" />
                </div>
                <div className="text-2xl font-bold">{c.value}</div>
                <div className="text-xs text-slate-500">{c.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Anlaşma Talepleri */}
      {partnershipRequests.length > 0 && (
        <Card className="border-primary ring-2 ring-primary/20" data-testid="partnership-requests-section">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/20 text-primary flex items-center justify-center">
                <Handshake className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Anlaşma Talepleri ({partnershipRequests.length})</h2>
                <p className="text-xs text-slate-500">Fotoğrafçılar sizinle çalışmak istiyor. Kabul edin, fiyat ve ürünlerinizi görsünler.</p>
              </div>
            </div>
            <div className="space-y-2">
              {partnershipRequests.map(pr => (
                <div key={pr.id} className="border rounded-xl p-3 bg-primary/5"
                  data-testid={`partnership-${pr.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-primary">{pr.photographer_name}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-600 mt-1 flex-wrap">
                        {pr.photographer_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{pr.photographer_phone}</span>}
                        {pr.photographer_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{pr.photographer_email}</span>}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Talep: {new Date(pr.requested_at).toLocaleString('tr-TR')}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => respondPartnership(pr.id, 'reject')}
                        disabled={processing[pr.id]} data-testid={`reject-${pr.id}`}>
                        Reddet
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => respondPartnership(pr.id, 'accept')}
                        disabled={processing[pr.id]} data-testid={`accept-${pr.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Kabul Et
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Baskıya Gelen - Yeni Siparişler */}
      <Card className="border-amber-200" data-testid="baskiya-gelen-section">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <Clock className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Baskıya Gelen</h2>
                <p className="text-xs text-slate-500">Fotoğrafçılardan yeni gelen siparişler · İşleme al ile devam et</p>
              </div>
            </div>
            <Link to="/albumcu/siparisler" className="text-xs text-primary hover:underline flex items-center gap-1">
              Tüm siparişler <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {incoming.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Yeni sipariş yok — tüm gelenleri işleme almışsınız</p>
            </div>
          ) : (
            <div className="space-y-2">
              {incoming.map(o => (
                <div key={o.id} className="border rounded-xl p-3 hover:border-amber-300 bg-amber-50/30 transition"
                  data-testid={`incoming-${o.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-primary">{o.photographer_name}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-medium">{o.product_name}</span>
                        <span className="text-emerald-600 font-bold text-sm">
                          {o.product_price?.toLocaleString('tr-TR')} TL
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-600 mt-1 flex-wrap">
                        {(o.bride_name || o.customer_name) && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {o.bride_name && o.groom_name ? `${o.bride_name} & ${o.groom_name}` : o.customer_name}
                          </span>
                        )}
                        {o.wedding_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(o.wedding_date).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                        <span>{(o.files || []).length} dosya</span>
                      </div>
                      {o.notes && (
                        <div className="text-xs bg-white border rounded p-1.5 mt-1 text-slate-700">
                          <b>Not:</b> {o.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Link to="/albumcu/siparisler">
                        <Button size="sm" variant="outline">
                          <Eye className="h-3.5 w-3.5 mr-1" />Aç
                        </Button>
                      </Link>
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => takeOrder(o)} disabled={processing[o.id]}
                        data-testid={`take-order-${o.id}`}>
                        {processing[o.id] ? '...' : 'İşleme Al'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
